import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle2,
  Circle,
  AlertCircle,
  BookOpen,
  ArrowRight,
  ScrollText,
  ChevronDown,
  ChevronRight,
  CalendarCheck,
  ClipboardCheck,
  X,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { HonestZeroNote } from "./CoverageEmptyStateNote";
import { useCurriculum, getStrandsForGradeSubject, getSpecificsForGradeSubject } from "../lib/curriculum";
import { isCurriculumSubjectVisible, profileSubjectsAsKeys, CANONICAL_TO_CURRICULUM, CANONICAL_SUBJECTS } from "../lib/subjects";
import {
  dismissSummativeSuggestion,
  listSummativeSuggestions,
} from "../lib/api";
import MinistrySampleTaskPopover from "./MinistrySampleTaskPopover";
import LongRangePlanProgressPanel from "./LongRangePlanProgressPanel";
import SkillFocusPanel from "./coverage-tracker/SkillFocusPanel";

// Ontario publishes Ministry sample tasks for Mathematics Grades 4-8.
// Outside that subject/grade band, the popover would always show
// "no published task", so we suppress the trigger entirely.
const _MIN_TASK_GRADES = new Set([
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
]);
const ministryTaskAvailable = (grade, subject) =>
  subject === "Mathematics" && _MIN_TASK_GRADES.has(grade);

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const SkillChip = ({ row, onViewLessons, onDraftComment }) => {
  const taught = (row.count || 0) > 0;
  const date = fmtDate(row.last_taught_at);
  const canView = taught && (row.activity_ids?.length || 0) > 0 && !!onViewLessons;
  const canDraft = taught && !!onDraftComment;
  const handleClick = () => {
    if (!canView) return;
    onViewLessons(row.name, row.activity_ids);
  };
  return (
    <li
      data-testid={`skill-chip-${row.name}`}
      className={`group flex items-start gap-3 rounded-lg border p-4 transition-colors ${
        taught
          ? "border-[#96ded1]/60 bg-[#96ded1]/10"
          : "border-[#4a6984]/15 bg-white"
      } ${canView ? "cursor-pointer hover:border-[#1c2d3d]/60 hover:bg-[#96ded1]/20" : ""}`}
      onClick={canView ? handleClick : undefined}
      onKeyDown={
        canView
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleClick();
              }
            }
          : undefined
      }
      role={canView ? "button" : undefined}
      tabIndex={canView ? 0 : undefined}
    >
      {taught ? (
        <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-[#1c2d3d]" />
      ) : (
        <Circle className="h-5 w-5 mt-0.5 shrink-0 text-[#4a6984]/30" />
      )}
      <div className="min-w-0 flex-1">
        <p
          className={`font-bold text-sm tracking-tight ${
            taught ? "text-[#1c2d3d]" : "text-[#4a6984]/70"
          }`}
        >
          {row.name}
        </p>
        <p
          className={`mt-1 text-xs font-medium ${
            taught ? "text-[#4a6984]" : "text-[#4a6984]/55"
          }`}
        >
          {taught
            ? `Taught in ${row.count} ${row.count === 1 ? "lesson" : "lessons"}${
                date ? ` · last on ${date}` : ""
              }`
            : "Not yet taught"}
        </p>
        {(canView || canDraft) && (
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            {canView && (
              <span
                data-testid={`skill-view-lessons-${row.name}`}
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.15em] text-[#1c2d3d] opacity-70 group-hover:opacity-100 transition-opacity"
              >
                View lessons <ArrowRight className="h-3 w-3" />
              </span>
            )}
            {canDraft && (
              <button
                type="button"
                data-testid={`skill-draft-comment-${row.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDraftComment(row.name);
                }}
                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.15em] text-[#4a6984] hover:text-[#1c2d3d] underline-offset-4 hover:underline transition-colors"
              >
                <ScrollText className="h-3 w-3" /> Draft comment
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

const SkillsPanel = ({ title, subtitle, rows, testid, onViewLessons, onDraftComment }) => (
  <section
    data-testid={testid}
    className="ae-card p-6 md:p-7"
  >
    <header className="mb-5">
      <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
        {title}
      </h3>
      <p className="mt-1 text-xs text-[#4a6984]/70 font-medium">{subtitle}</p>
    </header>
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {rows.map((r) => (
        <SkillChip
          key={r.name}
          row={r}
          onViewLessons={onViewLessons}
          onDraftComment={onDraftComment}
        />
      ))}
    </ul>
  </section>
);

const GapsCallout = ({ gaps }) => {
  const ts = gaps?.transferable_skills || [];
  const ls = gaps?.learning_skills || [];
  if (ts.length === 0 && ls.length === 0) return null;
  return (
    <section
      data-testid="coverage-gaps"
      className="ae-card border-l-4 border-l-[#c2410c] p-6 md:p-7"
    >
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 mt-0.5 shrink-0 text-[#c2410c]" />
        <div className="min-w-0 flex-1">
          <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
            Gaps to consider
          </h3>
          <p className="mt-1 text-sm text-[#4a6984]/80">
            You haven&apos;t generated a lesson touching these skills yet —
            something to plan into your next unit.
          </p>
          <div className="mt-4 space-y-3">
            {ts.length > 0 && (
              <div>
                <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#4a6984]/70 mb-1.5">
                  Transferable Skills
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="gap-transferable">
                  {ts.map((s) => (
                    <span
                      key={s}
                      className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-[#4a6984]/20 text-[#4a6984]"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {ls.length > 0 && (
              <div>
                <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#4a6984]/70 mb-1.5">
                  Learning Skills
                </p>
                <div className="flex flex-wrap gap-1.5" data-testid="gap-learning">
                  {ls.map((s) => (
                    <span
                      key={s}
                      className="text-xs font-semibold px-2.5 py-1 rounded-full bg-white border border-[#4a6984]/20 text-[#4a6984]"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Phase 114.C — Coverage Tracker → Summative Task Builder bridge.
// Surfaces a quiet "Assessment ready" pill on strand rows once a
// teacher has accumulated ≥3 taught lessons in that subject+strand
// AND no summative has been generated for the strand in the current
// reporting period yet.
//
// Per user override (Feb 2026): the indicator stays active across
// ALL reporting periods, Progress Report included — Ontario teachers
// still record marks during Sept-Nov and those marks carry forward.

const _PERIOD_LABELS = {
  progress_report: "Progress Report",
  term_1: "Term 1",
  term_2: "Term 2",
};

const SummativeReadyBadge = ({ suggestion, onDismiss }) => {
  const navigate = useNavigate();
  if (!suggestion) return null;
  const goToBuilder = () => {
    // The Generator form's URL prefill reducer reads ?mode=summative
    // plus grade / subject / strand / expectations. We pass the strand
    // letter (single letter — that's how the Strand dropdown is keyed)
    // and a comma-joined list of every taught code in that strand so
    // the form lands with all the evidence pre-attached.
    const params = new URLSearchParams({
      mode: "summative",
      grade: suggestion.grade,
      subject: suggestion.subject,
      strand: suggestion.strand_code,
      expectations: (suggestion.taught_expectations || []).join(","),
      reporting_period: suggestion.reporting_period,
    });
    navigate(`/generator?${params.toString()}`);
  };
  const handleDismiss = async (e) => {
    e.stopPropagation();
    try {
      await dismissSummativeSuggestion({
        subject: suggestion.subject,
        grade: suggestion.grade,
        strand_code: suggestion.strand_code,
        reporting_period: suggestion.reporting_period,
      });
      onDismiss?.(suggestion);
    } catch {
      toast.error("Couldn't dismiss that suggestion.");
    }
  };
  const periodLabel =
    _PERIOD_LABELS[suggestion.reporting_period] || suggestion.reporting_period;
  return (
    <span
      className="inline-flex items-stretch rounded-md border border-[#1c9e7e]/60 bg-[#96ded1]/15 text-[#1c2d3d] overflow-hidden"
      data-testid={`summative-ready-${suggestion.grade.replace(/\s+/g, "-")}-${suggestion.subject.replace(/\s+/g, "-")}-${suggestion.strand_code}-${suggestion.reporting_period}`}
    >
      <button
        type="button"
        onClick={goToBuilder}
        title={`${suggestion.taught_count} lessons taught in this strand this ${periodLabel.toLowerCase()} — open the Summative Task Builder pre-populated with these expectations.`}
        className="flex items-center gap-1.5 px-2 py-1 text-[10.5px] font-extrabold tracking-tight hover:bg-[#96ded1]/30 transition-colors"
      >
        <ClipboardCheck className="h-3 w-3" />
        Assessment ready
        <ArrowRight className="h-3 w-3" />
      </button>
      <button
        type="button"
        onClick={handleDismiss}
        title="Dismiss for this strand + reporting period"
        aria-label="Dismiss suggestion"
        className="border-l border-[#1c9e7e]/40 px-1.5 text-[#4a6984] hover:text-red-600 hover:bg-white/50"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
};

// Compact inline preview that sits directly under the strand title
// when a non-dismissed suggestion is active. Surfaces enough detail
// for a teacher to scan the tracker and decide whether to act,
// without needing to click through to the Builder.
//
// Phase 114.C.2 — each code carries an Ontario-grade precision marker:
//   ✓  the specific expectation was taught directly (appeared as e.g.
//      "B1.3" in a lesson's curriculum_links)
//   ·  the specific expectation was only touched via its parent
//      overall expectation (teacher taught "B1", which covers all
//      B1.* specifics in spirit but not by code)
// This lets the teacher sense at-a-glance whether enough specific-
// level evidence exists to anchor a summative.

const _PREVIEW_CODE_LIMIT = 6;

const _parentOverall = (code) => {
  if (!code) return "";
  const i = code.indexOf(".");
  return i > 0 ? code.slice(0, i) : code;
};

const SummativeReadyPreview = ({ suggestion, specsForStrand = [] }) => {
  if (!suggestion) return null;
  const periodLabel =
    _PERIOD_LABELS[suggestion.reporting_period] || suggestion.reporting_period;

  // Build the precision-annotated code list:
  //  - every directly-taught specific shows up with ✓
  //  - every specific under a directly-taught overall (but not itself
  //    taught) shows up with ·
  //  - directly-taught overalls also surface (with ✓) so nothing is
  //    silently dropped — useful when a strand has no specific codes
  //    in the curriculum data (older strands)
  const taughtSpecific = new Set(
    (suggestion.taught_specific_codes || []).map((c) => c.toUpperCase()),
  );
  const taughtOverall = new Set(
    (suggestion.taught_overall_codes || []).map((c) => c.toUpperCase()),
  );

  const annotated = [];
  const seen = new Set();
  // 1. Walk the curriculum's specifics for this strand; mark each.
  for (const sp of specsForStrand || []) {
    const code = (sp.code || "").toUpperCase();
    if (!code || seen.has(code)) continue;
    if (taughtSpecific.has(code)) {
      annotated.push({ code, mark: "✓", precise: true });
      seen.add(code);
    } else if (taughtOverall.has(_parentOverall(code))) {
      annotated.push({ code, mark: "·", precise: false });
      seen.add(code);
    }
  }
  // 2. Anything in taught_specific_codes not in the strand's curriculum
  //    (mismatched curriculum data, custom codes) still shows up — never
  //    silently drop teacher evidence.
  for (const c of taughtSpecific) {
    if (!seen.has(c)) {
      annotated.push({ code: c, mark: "✓", precise: true });
      seen.add(c);
    }
  }
  // 3. Directly-taught overalls themselves (no decimal sub-number) —
  //    surface alongside their specifics so they're visible even when
  //    no specifics exist for the strand.
  for (const c of taughtOverall) {
    if (!seen.has(c)) {
      annotated.push({ code: c, mark: "✓", precise: true });
      seen.add(c);
    }
  }
  // Stable display order: ticked codes first (they're what anchor a
  // summative), then parent-only codes — within each group, sorted
  // alphanumerically.
  annotated.sort((a, b) => {
    if (a.precise !== b.precise) return a.precise ? -1 : 1;
    return a.code.localeCompare(b.code);
  });
  const shown = annotated.slice(0, _PREVIEW_CODE_LIMIT);
  const overflow = annotated.length - shown.length;
  const preciseCount = annotated.filter((x) => x.precise).length;

  return (
    <p
      className="mt-1 text-[10.5px] font-medium text-[#4a6984]/85 leading-snug"
      data-testid={`summative-ready-preview-${suggestion.grade.replace(/\s+/g, "-")}-${suggestion.subject.replace(/\s+/g, "-")}-${suggestion.strand_code}-${suggestion.reporting_period}`}
    >
      <span className="font-extrabold text-[#1c2d3d]">
        {suggestion.taught_count} lesson{suggestion.taught_count === 1 ? "" : "s"} taught
      </span>
      <span className="text-[#4a6984]/55"> · </span>
      <span className="tabular-nums tracking-tight">
        {shown.map((x, i) => (
          <span
            key={x.code}
            className={x.precise ? "text-[#1c2d3d]" : "text-[#4a6984]/70 italic"}
            title={
              x.precise
                ? "Specific expectation taught directly"
                : "Touched only via the parent overall expectation"
            }
          >
            {i > 0 ? <span className="text-[#4a6984]/40">, </span> : null}
            {x.code}
            <span
              className={`ml-0.5 ${x.precise ? "text-[#1c9e7e]" : "text-[#4a6984]/45"}`}
            >
              {x.mark}
            </span>
          </span>
        ))}
        {overflow > 0 ? (
          <span className="text-[#4a6984]/65"> +{overflow} more</span>
        ) : null}
      </span>
      <span className="text-[#4a6984]/55"> · {periodLabel}</span>
      {annotated.length > 0 && preciseCount < annotated.length ? (
        <span className="text-[#4a6984]/55">
          {" "}
          ·{" "}
          <span className="font-bold text-[#1c2d3d]">
            {preciseCount}/{annotated.length}
          </span>{" "}
          specific
        </span>
      ) : null}
    </p>
  );
};

const StrandProgressBar = ({ covered, total }) => {
  const hasTotal = typeof total === "number" && total > 0;
  const pct = hasTotal ? Math.min(100, Math.round((covered / total) * 100)) : null;
  return (
    <div
      data-testid="strand-progress"
      className="mt-2 mb-3 flex items-center gap-3"
    >
      <div className="flex-1 h-1.5 rounded-full bg-[#4a6984]/10 overflow-hidden">
        <div
          className="h-full rounded-full bg-[#96ded1] transition-[width]"
          style={{ width: hasTotal ? `${pct}%` : `${Math.min(100, covered * 8)}%` }}
        />
      </div>
      <span className="text-[11px] font-bold text-[#4a6984] tabular-nums whitespace-nowrap">
        {hasTotal ? (
          <>
            {covered} of {total}
            <span className="text-[#4a6984]/60 font-semibold"> · {pct}%</span>
          </>
        ) : (
          <>{covered} taught</>
        )}
      </span>
    </div>
  );
};

// Honest-zero copy for the Coverage Tracker lives in its own module
// (`CoverageEmptyStateNote`) so it can be rendered in isolation by the
// behaviour tripwire without pulling in this file's heavy imports.



const ExpectationsPanel = ({ rows, strandProgress = [], onViewLessons, totalBuilt = 0 }) => {
  const { user, openProfileModal } = useAuth();
  const { data: curriculum } = useCurriculum();

  const grades = user?.grades_taught || [];
  const subjectsTaught = user?.subjects_taught || [];
  const hasProfile = grades.length > 0 && subjectsTaught.length > 0;

  // Phase 116.B — derive the rendered subject list from the Class
  // Profile via the canonical filter. A teacher's profile stores
  // canonical labels ("Visual Arts", "Health", …) which don't map
  // 1:1 to curriculum-side subject strings ("The Arts", "Health and
  // Physical Education", …). We translate canonical-keys → curriculum
  // subjects, then intersect with the curriculum subjects that
  // actually exist for the teacher's grade band, then dedupe + order
  // canonically. Empty profile → default-show-all.
  const subjects = useMemo(() => {
    const allowedCurr = new Set();
    if (curriculum && grades.length) {
      for (const g of grades) {
        for (const cs of (curriculum?.SUBJECTS_BY_GRADE?.[g] || [])) {
          allowedCurr.add(cs);
        }
      }
    }
    const profileSet = profileSubjectsAsKeys(subjectsTaught);
    const isDefault = profileSet.size === 0;
    const ordered = [];
    const seen = new Set();
    for (const canon of CANONICAL_SUBJECTS) {
      if (!isDefault && !profileSet.has(canon.key)) continue;
      for (const cs of (CANONICAL_TO_CURRICULUM[canon.key] || [])) {
        if (allowedCurr.size > 0 && !allowedCurr.has(cs)) continue;
        if (seen.has(cs)) continue;
        seen.add(cs);
        ordered.push(cs);
      }
    }
    return ordered;
  }, [grades, subjectsTaught, curriculum]);

  // Phase 114.C — pull summative suggestions once, index by
  // `${grade}|${subject}|${strand_code}` so the strand renderer can do
  // a constant-time lookup. Non-Pro users get an empty list (the
  // backend silently returns `[]` for non-paid sessions) so the UI
  // surface vanishes for them without an explicit gate.
  const [suggestions, setSuggestions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    listSummativeSuggestions()
      .then((res) => {
        if (cancelled) return;
        setSuggestions(res?.suggestions || []);
      })
      .catch(() => {
        if (cancelled) return;
        setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  const suggestionsByStrand = useMemo(() => {
    const map = {};
    for (const s of suggestions) {
      if (s.dismissed) continue;
      const key = `${s.grade}|${s.subject}|${s.strand_code}`;
      (map[key] = map[key] || []).push(s);
    }
    return map;
  }, [suggestions]);
  const handleDismissSuggestion = (s) => {
    setSuggestions((prev) =>
      prev.map((x) =>
        x.grade === s.grade &&
        x.subject === s.subject &&
        x.strand_code === s.strand_code &&
        x.reporting_period === s.reporting_period
          ? { ...x, dismissed: true }
          : x,
      ),
    );
  };

  // Index taught rows by (grade|subject|strandCode) → [rows]
  const taughtByStrand = {};
  for (const r of rows) {
    const code = (r.strand_code || r.strand || "").trim().toUpperCase().charAt(0);
    const key = `${r.grade}|${r.subject}|${code}`;
    (taughtByStrand[key] = taughtByStrand[key] || []).push(r);
  }
  const progressByKey = {};
  for (const p of strandProgress) {
    progressByKey[`${p.grade}|${p.subject}|${(p.strand_code || "").toUpperCase()}`] = p;
  }

  return (
    <section
      data-testid="coverage-expectations"
      className="ae-card p-6 md:p-7"
    >
      <header className="mb-5 flex items-start gap-3">
        <span className="h-9 w-9 shrink-0 rounded-md bg-[#f4f5f7] border border-[#4a6984]/15 grid place-items-center">
          <BookOpen className="h-4 w-4 text-[#1c2d3d]" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight">
            Expectations covered
          </h3>
          <p className="mt-1 text-xs text-[#4a6984]/70 font-medium">
            {hasProfile
              ? `Scoped to your classroom — ${grades.length} ${grades.length === 1 ? "grade" : "grades"} · ${subjects.length} ${subjects.length === 1 ? "subject" : "subjects"}.`
              : "Every Ontario expectation code you've marked taught — grouped by strand."}
          </p>
        </div>
        {hasProfile && (
          <button
            type="button"
            onClick={openProfileModal}
            data-testid="expectations-edit-profile"
            className="shrink-0 text-xs font-semibold text-[#4a6984] hover:text-[#1c2d3d] underline-offset-4 hover:underline"
          >
            Edit classroom
          </button>
        )}
      </header>

      {!hasProfile ? (
        <ProfileEmptyState
          rows={rows}
          strandProgress={strandProgress}
          onViewLessons={onViewLessons}
          openProfileModal={openProfileModal}
          totalBuilt={totalBuilt}
        />
      ) : (
        <div className="space-y-3" data-testid="expectations-subjects">
          {(rows || []).length === 0 && (
            <div
              className="rounded-md border border-[#4a6984]/15 bg-[#f4f5f7] px-3.5 py-3 mb-1"
              data-testid="coverage-honest-zero"
            >
              <HonestZeroNote totalBuilt={totalBuilt} testid="coverage-honest-zero-note" />
            </div>
          )}
          {subjects.map((subject) => (
            <SubjectCard
              key={subject}
              subject={subject}
              grades={grades}
              curriculum={curriculum}
              taughtByStrand={taughtByStrand}
              progressByKey={progressByKey}
              onViewLessons={onViewLessons}
              suggestionsByStrand={suggestionsByStrand}
              onDismissSuggestion={handleDismissSuggestion}
            />
          ))}
        </div>
      )}
    </section>
  );
};

const SubjectCard = ({
  subject,
  grades,
  curriculum,
  taughtByStrand,
  progressByKey,
  onViewLessons,
  suggestionsByStrand = {},
  onDismissSuggestion,
}) => {
  const [open, setOpen] = useState(false);
  // Total taught codes across this subject (any grade).
  let taughtTotal = 0;
  let strandTotal = 0;
  let strandCovered = 0;
  for (const grade of grades) {
    const strands = getStrandsForGradeSubject(curriculum, grade, subject);
    for (const s of strands) {
      strandTotal += 1;
      const k = `${grade}|${subject}|${s.code.toUpperCase()}`;
      const rows = taughtByStrand[k] || [];
      if (rows.length > 0) strandCovered += 1;
      taughtTotal += rows.reduce((acc, r) => acc + (r.count || 0), 0);
    }
  }
  const noStrandsKnown = strandTotal === 0;
  const testid = `expectations-subject-${subject.replace(/\s+/g, "-")}`;

  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-[#4a6984]/15 bg-white overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`${testid}-toggle`}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-[#f4f5f7]/60 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="h-7 w-7 shrink-0 rounded-md bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d]">
            {open ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </span>
          <div className="text-left min-w-0">
            <p className="font-extrabold text-sm text-[#1c2d3d] tracking-tight truncate">
              {subject}
            </p>
            <p className="text-[11px] font-semibold text-[#4a6984]/70 mt-0.5">
              {noStrandsKnown
                ? `${taughtTotal} expectation${taughtTotal === 1 ? "" : "s"} taught`
                : `${strandCovered} of ${strandTotal} strands touched · ${taughtTotal} expectation${taughtTotal === 1 ? "" : "s"} taught`}
            </p>
          </div>
        </div>
        {strandTotal > 0 && (
          <div className="w-32 shrink-0">
            <div className="h-1.5 rounded-full bg-[#4a6984]/10 overflow-hidden">
              <div
                className="h-full bg-[#96ded1]"
                style={{ width: `${Math.round((strandCovered / strandTotal) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </button>

      {open && (
        <div className="border-t border-[#4a6984]/10 bg-[#f4f5f7]/40 px-5 py-5 space-y-5">
          {grades.map((grade) => {
            const strands = getStrandsForGradeSubject(curriculum, grade, subject);
            if (strands.length === 0) {
              return (
                <div key={grade}>
                  <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#4a6984]/70">
                    {grade}
                  </p>
                  <p className="mt-1.5 text-xs italic text-[#4a6984]/60">
                    {subject} is not part of this grade&apos;s Ontario curriculum.
                  </p>
                </div>
              );
            }
            return (
              <div key={grade} data-testid={`${testid}-grade-${grade.replace(/\s+/g, "-")}`}>
                <p className="font-bold uppercase text-[10px] tracking-[0.22em] text-[#4a6984]/80 mb-3">
                  {grade}
                </p>
                <div className="space-y-3">
                  {strands.map((s) => {
                    const code = s.code.toUpperCase();
                    const k = `${grade}|${subject}|${code}`;
                    const taughtCodes = taughtByStrand[k] || [];
                    const prog = progressByKey[k];
                    const isTouched = taughtCodes.length > 0;
                    // Build a (specific-expectation-code → taught row) lookup
                    // so the checklist can render every spec under this
                    // strand letter as a real checkbox, ticked when taught.
                    const specsForSubject = getSpecificsForGradeSubject(
                      curriculum,
                      grade,
                      subject,
                    );
                    const specsForStrand = specsForSubject.filter(
                      (sp) => (sp.code || "").charAt(0).toUpperCase() === code,
                    );
                    const taughtByCode = {};
                    for (const r of taughtCodes) {
                      taughtByCode[(r.code || "").toUpperCase()] = r;
                    }
                    const hasSpecChecklist = specsForStrand.length > 0;
                    return (
                      <div
                        key={s.code}
                        data-testid={`${testid}-strand-${grade.replace(/\s+/g, "-")}-${code}`}
                        className={`rounded-lg border p-3.5 ${
                          isTouched
                            ? "border-[#96ded1]/70 bg-white"
                            : "border-[#4a6984]/12 bg-white"
                        }`}
                      >
                        <div className="flex items-start gap-2.5">
                          {isTouched ? (
                            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-[#1c2d3d]" />
                          ) : (
                            <Circle className="h-4 w-4 mt-0.5 shrink-0 text-[#4a6984]/25" />
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2 flex-wrap">
                              <p
                                className={`font-bold text-sm tracking-tight ${
                                  isTouched ? "text-[#1c2d3d]" : "text-[#4a6984]/75"
                                }`}
                              >
                                {s.code} — {s.name}
                              </p>
                              {(suggestionsByStrand[k] || []).map((sg) => (
                                <SummativeReadyBadge
                                  key={`${sg.reporting_period}`}
                                  suggestion={sg}
                                  onDismiss={onDismissSuggestion}
                                />
                              ))}
                            </div>
                            {(suggestionsByStrand[k] || []).map((sg) => (
                              <SummativeReadyPreview
                                key={`preview-${sg.reporting_period}`}
                                suggestion={sg}
                                specsForStrand={specsForStrand}
                              />
                            ))}
                            {prog && typeof prog.total === "number" && prog.total > 0 ? (
                              <StrandProgressBar covered={prog.covered} total={prog.total} />
                            ) : hasSpecChecklist ? (
                              <StrandProgressBar
                                covered={specsForStrand.reduce(
                                  (acc, sp) =>
                                    acc + (taughtByCode[sp.code.toUpperCase()] ? 1 : 0),
                                  0,
                                )}
                                total={specsForStrand.length}
                              />
                            ) : (
                              <p className="mt-1 text-[11px] font-semibold text-[#4a6984]/60">
                                {isTouched
                                  ? `${taughtCodes.length} expectation${taughtCodes.length === 1 ? "" : "s"} taught`
                                  : "Not yet taught"}
                              </p>
                            )}
                            {hasSpecChecklist ? (
                              <ul
                                data-testid={`spec-list-${grade.replace(/\s+/g, "-")}-${subject.replace(/\s+/g, "-")}-${code}`}
                                className="mt-3 space-y-1.5"
                              >
                                {specsForStrand.map((sp) => {
                                  const spCode = (sp.code || "").toUpperCase();
                                  const tr = taughtByCode[spCode];
                                  const taught = !!tr;
                                  const ids = tr?.activity_ids || [];
                                  const canView = taught && ids.length > 0 && !!onViewLessons;
                                  const handle = () => {
                                    if (!canView) return;
                                    onViewLessons(
                                      `${subject} · ${spCode}`,
                                      ids,
                                    );
                                  };
                                  return (
                                    <li
                                      key={sp.code}
                                      data-testid={`spec-row-${grade.replace(/\s+/g, "-")}-${subject.replace(/\s+/g, "-")}-${spCode}`}
                                      onClick={canView ? handle : undefined}
                                      onKeyDown={
                                        canView
                                          ? (e) => {
                                              if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                handle();
                                              }
                                            }
                                          : undefined
                                      }
                                      role={canView ? "button" : undefined}
                                      tabIndex={canView ? 0 : undefined}
                                      className={`flex items-start gap-2 rounded-md px-2 py-1.5 transition-colors ${
                                        taught
                                          ? "bg-[#96ded1]/20 border border-[#96ded1]/50"
                                          : "bg-[#f4f5f7]/40 border border-transparent"
                                      } ${canView ? "cursor-pointer hover:bg-[#96ded1]/35" : ""}`}
                                    >
                                      {taught ? (
                                        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#1c2d3d]" />
                                      ) : (
                                        <Circle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#4a6984]/30" />
                                      )}
                                      <div className="min-w-0 flex-1">
                                        <p
                                          className={`text-[12px] font-bold tracking-tight flex items-center flex-wrap gap-1.5 ${
                                            taught ? "text-[#1c2d3d]" : "text-[#4a6984]/85"
                                          }`}
                                        >
                                          <span className="font-extrabold">{sp.code}</span>
                                          {ministryTaskAvailable(grade, subject) && (
                                            <MinistrySampleTaskPopover
                                              grade={grade}
                                              code={sp.code}
                                              label={sp.topic || sp.code}
                                            />
                                          )}
                                          {sp.topic && (
                                            <span className="ml-0.5 font-semibold text-[#4a6984]/80">
                                              · {sp.topic}
                                            </span>
                                          )}
                                          {taught && tr.count > 1 && (
                                            <span className="ml-0.5 text-[10px] font-bold text-[#4a6984]/70">
                                              ×{tr.count}
                                            </span>
                                          )}
                                        </p>
                                        {sp.description && (
                                          <p
                                            className={`text-[11px] mt-0.5 leading-snug ${
                                              taught ? "text-[#1c2d3d]/85" : "text-[#4a6984]/65"
                                            }`}
                                          >
                                            {sp.description}
                                          </p>
                                        )}
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            ) : (
                              isTouched && (
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  {taughtCodes.map((c) => {
                                    const date = fmtDate(c.last_taught_at);
                                    const ids = c.activity_ids || [];
                                    const canView = ids.length > 0 && !!onViewLessons;
                                    const label = `${c.subject ? `${c.subject} · ` : ""}${c.code}`;
                                    const baseCls =
                                      "inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-[#96ded1]/25 border border-[#96ded1]/60 text-[#1c2d3d]";
                                    if (canView) {
                                      return (
                                        <button
                                          type="button"
                                          key={c.code}
                                          data-testid={`expectation-${c.grade}-${c.subject}-${c.code}`}
                                          onClick={() => onViewLessons(label, ids)}
                                          title={
                                            c.sample_activity_name
                                              ? `${c.sample_activity_name}${date ? ` · ${date}` : ""}`
                                              : undefined
                                          }
                                          className={`${baseCls} hover:bg-[#96ded1]/45 transition-colors`}
                                        >
                                          <CheckCircle2 className="h-3 w-3" />
                                          {c.code}
                                          {c.count > 1 && (
                                            <span className="ml-0.5 text-[10px] font-bold text-[#4a6984]/70">
                                              ×{c.count}
                                            </span>
                                          )}
                                        </button>
                                      );
                                    }
                                    return (
                                      <span
                                        key={c.code}
                                        className={baseCls}
                                        title={
                                          c.sample_activity_name
                                            ? `${c.sample_activity_name}${date ? ` · ${date}` : ""}`
                                            : undefined
                                        }
                                      >
                                        <CheckCircle2 className="h-3 w-3" />
                                        {c.code}
                                      </span>
                                    );
                                  })}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Fallback for users without a profile set (anonymous or skipped onboarding).
// Renders the legacy flat grouping by Grade > Subject > Strand of taught codes.
const ProfileEmptyState = ({ rows, strandProgress, onViewLessons, openProfileModal, totalBuilt = 0 }) => {
  const grouped = {};
  for (const r of rows) {
    const gKey = r.grade || "Ungraded";
    const sKey = r.subject || "Other";
    const strandKey = r.strand || "—";
    grouped[gKey] = grouped[gKey] || {};
    grouped[gKey][sKey] = grouped[gKey][sKey] || {};
    grouped[gKey][sKey][strandKey] = grouped[gKey][sKey][strandKey] || [];
    grouped[gKey][sKey][strandKey].push(r);
  }
  const grades = Object.keys(grouped).sort();
  const progressByKey = {};
  for (const p of strandProgress) {
    progressByKey[`${p.grade}|${p.subject}|${p.strand_label}`] = p;
  }

  return (
    <>
      {openProfileModal && (
        <div
          data-testid="expectations-profile-cta"
          className="mb-5 rounded-lg border border-[#96ded1]/60 bg-[#96ded1]/15 p-4 flex items-start gap-3"
        >
          <BookOpen className="h-4 w-4 mt-0.5 shrink-0 text-[#1c2d3d]" />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-sm text-[#1c2d3d]">
              Set your classroom profile
            </p>
            <p className="mt-0.5 text-xs text-[#4a6984]/80">
              Pick the grades and subjects you actually teach and we&apos;ll
              show every Ontario strand in a collapsible checklist — green ✓
              for the ones you&apos;ve covered.
            </p>
            <button
              type="button"
              onClick={openProfileModal}
              data-testid="expectations-set-profile"
              className="mt-2 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-[#1c2d3d] hover:underline underline-offset-4"
            >
              Set profile <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
      {rows.length === 0 ? (
        <HonestZeroNote totalBuilt={totalBuilt} testid="coverage-empty-state" />
      ) : (
        <div className="space-y-6">
          {grades.map((g) => (
            <div key={g}>
              <p className="font-bold uppercase text-[11px] tracking-[0.2em] text-[#4a6984] mb-3">
                {g}
              </p>
              <div className="space-y-4 pl-1">
                {Object.keys(grouped[g]).sort().map((sub) => (
                  <div key={sub}>
                    <p className="font-extrabold text-sm text-[#1c2d3d] tracking-tight mb-2">
                      {sub}
                    </p>
                    <div className="space-y-3 pl-3 border-l border-[#4a6984]/15">
                      {Object.keys(grouped[g][sub]).sort().map((str) => {
                        const codes = grouped[g][sub][str];
                        const progress = progressByKey[`${g}|${sub}|${str}`];
                        return (
                          <div key={str}>
                            <p className="text-xs font-bold text-[#4a6984]/80 mb-1">
                              {str}
                            </p>
                            {progress && (
                              <StrandProgressBar
                                covered={progress.covered}
                                total={progress.total}
                              />
                            )}
                            <div className="flex flex-wrap gap-1.5">
                              {codes.map((c) => {
                                const date = fmtDate(c.last_taught_at);
                                const ids = c.activity_ids || [];
                                const canView = ids.length > 0 && !!onViewLessons;
                                const label = `${c.subject ? `${c.subject} · ` : ""}${c.code}`;
                                const baseCls =
                                  "inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-md bg-[#96ded1]/25 border border-[#96ded1]/60 text-[#1c2d3d]";
                                if (canView) {
                                  return (
                                    <button
                                      type="button"
                                      key={c.code}
                                      data-testid={`expectation-${c.grade}-${c.subject}-${c.code}`}
                                      onClick={() => onViewLessons(label, ids)}
                                      title={
                                        c.sample_activity_name
                                          ? `${c.sample_activity_name}${date ? ` · ${date}` : ""}`
                                          : undefined
                                      }
                                      className={`${baseCls} hover:bg-[#96ded1]/45 transition-colors`}
                                    >
                                      <CheckCircle2 className="h-3 w-3" />
                                      {c.code}
                                      {c.count > 1 && (
                                        <span className="ml-0.5 text-[10px] font-bold text-[#4a6984]/70">
                                          ×{c.count}
                                        </span>
                                      )}
                                    </button>
                                  );
                                }
                                return (
                                  <span
                                    key={c.code}
                                    data-testid={`expectation-${c.grade}-${c.subject}-${c.code}`}
                                    title={
                                      c.sample_activity_name
                                        ? `${c.sample_activity_name}${date ? ` · ${date}` : ""}`
                                        : undefined
                                    }
                                    className={baseCls}
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                    {c.code}
                                    {c.count > 1 && (
                                      <span className="ml-0.5 text-[10px] font-bold text-[#4a6984]/70">
                                        ×{c.count}
                                      </span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// Filter coverage detail rows down to only the activities that have
// been "taught" (scheduled on the planner with a date ≤ today).
// Returns a shallow clone of `coverage` with `count` + `activity_ids`
// reduced to the taught subset on every detail row.
const filterTaught = (coverage, taughtSet) => {
  if (!coverage) return coverage;
  // Covered requires taught: keep a row only if it rides at least one lesson
  // the teacher marked taught. Dropping zero-count rows (not just zeroing the
  // count) is what makes the checkmarks, strand bars, %, and counts all honour
  // the toggle — the old map-but-keep left ticks showing with a 0 count.
  const filterRows = (rows) =>
    (rows || [])
      .map((r) => {
        const ids = (r.activity_ids || []).filter((id) => taughtSet.has(id));
        return { ...r, activity_ids: ids, count: ids.length };
      })
      .filter((r) => r.count > 0);
  const expectations_detail = filterRows(coverage.expectations_detail);
  // Recompute each strand's `covered` from the taught-only rows so the strand
  // progress bars match the visible checkmarks.
  const coveredByStrand = {};
  for (const r of expectations_detail) {
    const letter = (r.strand_code || r.strand || "").trim().toUpperCase().charAt(0);
    const k = `${r.grade}|${r.subject}|${letter}`;
    (coveredByStrand[k] = coveredByStrand[k] || new Set()).add(r.code);
  }
  const strand_progress = (coverage.strand_progress || []).map((p) => {
    const k = `${p.grade}|${p.subject}|${(p.strand_code || "").toUpperCase()}`;
    return { ...p, covered: coveredByStrand[k] ? coveredByStrand[k].size : 0 };
  });
  return {
    ...coverage,
    transferable_detail: filterRows(coverage.transferable_detail),
    learning_detail: filterRows(coverage.learning_detail),
    expectations_detail,
    strand_progress,
  };
};

export const CoverageTracker = ({ coverage, onViewLessons, onDraftComment }) => {
  // Phase 83c — "Taught only" filter, driven by the planner.
  // Default ON if the user has a planner and at least one taught lesson;
  // remembered in localStorage so users opting out stick to "All".
  const hasPlanner = !!coverage?.has_planner;
  const taughtIds = useMemo(
    () => new Set(coverage?.taught_activity_ids || []),
    [coverage?.taught_activity_ids],
  );
  const [taughtOnly, setTaughtOnly] = useState(() => {
    try {
      const stored = window.localStorage.getItem("ae_tracker_taught_only");
      if (stored !== null) return stored === "1";
    } catch { /* localStorage may be unavailable */ }
    return true; // Covered-requires-taught: Taught-only is the honest default.
  });

  const flipTaughtOnly = (v) => {
    setTaughtOnly(v);
    try { window.localStorage.setItem("ae_tracker_taught_only", v ? "1" : "0"); } catch { /* ignore */ }
  };

  const filteredCoverage = useMemo(
    () => (taughtOnly ? filterTaught(coverage, taughtIds) : coverage),
    [coverage, taughtOnly, taughtIds],
  );

  if (!coverage) return null;
  return (
    <div className="space-y-6" data-testid="coverage-tracker">
      <div className="ae-card p-3" data-testid="tracker-taught-toggle">
        <div className="flex items-center justify-between gap-3">
          <div className="text-xs text-[#4a6984] flex items-center gap-2">
            <CalendarCheck className="h-4 w-4 text-[#1c2d3d]" />
            <span>
              <strong>{taughtIds.size}</strong> lesson{taughtIds.size === 1 ? "" : "s"} marked taught so far.
            </span>
          </div>
          <div className="inline-flex rounded-lg bg-[#f4f5f7] p-1">
            <button
              type="button"
              onClick={() => flipTaughtOnly(true)}
              className={`px-3 py-1 text-xs font-semibold rounded ${taughtOnly ? "bg-white shadow-sm text-[#1c2d3d]" : "text-[#4a6984]"}`}
              data-testid="tracker-filter-taught"
            >
              Taught (your coverage)
            </button>
            <button
              type="button"
              onClick={() => flipTaughtOnly(false)}
              className={`px-3 py-1 text-xs font-semibold rounded ${!taughtOnly ? "bg-white shadow-sm text-[#1c2d3d]" : "text-[#4a6984]"}`}
              data-testid="tracker-filter-all"
            >
              All generated (built, not taught)
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs text-[#4a6984] leading-relaxed" data-testid="tracker-view-caption">
          {taughtOnly ? (
            <>Showing the curriculum you've <strong>marked taught</strong> — this is your real coverage. A lesson counts here only once you mark it taught in My Library.</>
          ) : (
            <><strong>Not your coverage.</strong> This view shows everything you've <strong>generated</strong>; a checkmark means a lesson was <strong>built</strong> for that expectation, not that it has been taught. Switch to “Taught” for real coverage.</>
          )}
        </p>
      </div>
      <GapsCallout gaps={filteredCoverage.gaps} />
      <LongRangePlanProgressPanel />
      <SkillsPanel
        title="Transferable Skills"
        subtitle="Ontario's 7 transferable skills — the cross-curricular capacities your lessons are building."
        rows={filteredCoverage.transferable_detail || []}
        testid="tracker-transferable"
        onViewLessons={onViewLessons}
      />
      {/* Lane 2 follow-up (Feb 09 2026) — Skill Focus panel. Slotted
          between the two SkillsPanels: Skill Focus is the transferable-
          skill you picked at generator time (from the same 7-item
          picker), so it sits naturally next to the Transferable Skills
          panel that reports what the lessons actually taught. Absolute
          bars scaled to `total_activities`, canonical labels — matches
          the founder brief. Now that this lives here, Home is free to
          stay lean. */}
      <SkillFocusPanel coverage={filteredCoverage} />
      <SkillsPanel
        title="Learning Skills & Work Habits"
        subtitle="Growing Success' 6 learning skills — the habits reported on the term report card."
        rows={filteredCoverage.learning_detail || []}
        testid="tracker-learning"
        onViewLessons={onViewLessons}
        onDraftComment={onDraftComment}
      />
      <ExpectationsPanel
        rows={filteredCoverage.expectations_detail || []}
        strandProgress={filteredCoverage.strand_progress || []}
        onViewLessons={onViewLessons}
        totalBuilt={coverage?.total_activities || 0}
      />
    </div>
  );
};

export default CoverageTracker;
